import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
// @mui
import { Box, Typography, Stack, Paper } from '@mui/material';
// components
import Logo from '../components/logo';
import { GloginComponent } from '../components/glogin';

// ----------------------------------------------------------------------

const LOGIN_IMAGES = 7;

// Safari/iOS reject "2026-07-13 11:25:46 +0300" from new Date(). Normalize to a
// format all engines parse (ISO 8601: "YYYY-MM-DDTHH:mm:ss+03:00").
function safeFormatDate(raw) {
  if (!raw) return '';
  let d = new Date(raw);
  if (isNaN(d.getTime())) {
    const iso = String(raw).trim().replace(' ', 'T').replace(/ ([+-]\d{2})(\d{2})$/, '$1:$2');
    d = new Date(iso);
  }
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function LoginPage() {
  const [info, setInfo] = useState(null);
  // Show a notice when redirected here after a session expired (see UserContext).
  const sessionExpired = (() => {
    try { return new URLSearchParams(window.location.search).get('reason') === 'expired'; }
    catch { return false; }
  })();

  useEffect(() => {
    fetch('/v1/info')
      .then((r) => r.json())
      .then(setInfo)
      .catch(() => {});
  }, []);

  // Pick background image based on version hash — changes each deploy
  const imgIndex = info?.version
    ? (info.version.split('').reduce((s, c) => s + c.charCodeAt(0), 0) % LOGIN_IMAGES) + 1
    : Math.floor(Math.random() * LOGIN_IMAGES) + 1;

  return (
    <>
      <Helmet>
        <title>Login | TERRRA</title>
      </Helmet>

      {/* Full-screen background */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background image */}
        <Box
          component="img"
          src={`/assets/images/login-bg-${imgIndex}.jpg`}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        />

        {/* Dark overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(0, 0, 0, 0.35)',
            zIndex: 1,
          }}
        />

        {/* Login card */}
        <Paper
          elevation={12}
          sx={{
            position: 'relative',
            zIndex: 2,
            width: { xs: '85%', sm: 340 },
            p: { xs: 3, sm: 3.5 },
            borderRadius: 3,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            textAlign: 'center',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Logo disabledLink sx={{ width: 56, height: 56 }} />
          </Box>

          <Typography variant="h4" sx={{ color: '#2870F5', fontWeight: 700, mb: 0.5 }}>
            TERRRA
          </Typography>

          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Unleashing Nature
          </Typography>

          {sessionExpired && (
            <Typography
              variant="caption"
              sx={{
                display: 'block', mb: 2, px: 1.5, py: 0.75, borderRadius: 1,
                bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600,
              }}
            >
              Your session expired — please sign in again.
            </Typography>
          )}

          <Stack direction="row" justifyContent="center" spacing={2}>
            <GloginComponent />
          </Stack>

          {/* Environment + version */}
          {info && (
            <Stack
              direction="row"
              justifyContent="center"
              spacing={1}
              sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
            >
              <Typography
                variant="caption"
                sx={{
                  px: 1.5,
                  py: 0.25,
                  borderRadius: 1,
                  fontWeight: 600,
                  bgcolor: info.env === 'production' ? '#e8f5e9' : info.env === 'staging' ? '#fff3e0' : '#e3f2fd',
                  color: info.env === 'production' ? '#2e7d32' : info.env === 'staging' ? '#e65100' : '#1565c0',
                }}
              >
                {info.env}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                v{info.version}
                {info.deployedAt && safeFormatDate(info.deployedAt) && (
                  <> · {safeFormatDate(info.deployedAt)}</>
                )}
              </Typography>
            </Stack>
          )}
        </Paper>
      </Box>
    </>
  );
}
